import { supabase } from "@/integrations/supabase/client";
import { getEffectiveUserId } from "@/utils/anonId";
import { contributeToSegment } from "@/api/roadSegments";
import { notifyContribution } from "@/api/notifyContribution";
import type { TravelMode } from "@/api/routing";
import type { LngLat } from "@/utils/polyline";
import { appVersion } from "@/lib/env";
import { enqueueTag, enqueueFeedback } from "@/utils/pendingContributions";

/**
 * Detect errors that mean "the write didn't reach the server cleanly" so
 * we know to queue the contribution for retry instead of throwing it away.
 * Covers offline browsers, fetch/network failures, transient 5xx responses,
 * and PostgREST auth/RLS hiccups that resolve once the user signs in.
 */
function isRetryableWriteError(err: unknown): boolean {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  if (!err || typeof err !== "object") return false;
  const e = err as { name?: string; message?: string; code?: string; status?: number; statusCode?: number };
  if (e.name === "TypeError") return true; // fetch failed (network)
  const msg = (e.message ?? "").toLowerCase();
  if (
    msg.includes("failed to fetch") ||
    msg.includes("networkerror") ||
    msg.includes("load failed") ||
    msg.includes("network request failed") ||
    msg.includes("offline")
  ) return true;
  const status = e.status ?? e.statusCode;
  if (typeof status === "number" && (status === 0 || status === 408 || status === 429 || status >= 500)) return true;
  // PostgREST auth/RLS — likely fixable by signing in.
  if (e.code === "PGRST301" || e.code === "PGRST302" || e.code === "42501") return true;
  return false;
}
import {
  Smile, Trees, Volume2, VolumeX, Users, AlertTriangle, Accessibility,
  type LucideIcon,
} from "lucide-react";

export type Sentiment = "positive" | "negative";

export interface CommunityTag {
  id: string;
  /** Not exposed in public reads — only present on rows the current user owns. */
  user_id?: string;
  lat: number;
  lng: number;
  travel_mode: string;
  tag_label: string;
  sentiment: string;
  created_at: string;
  pleasantness?: number | null;
  report_kind?: string | null;
  reporter_weight?: number;
}

/** Quick one-tap report kinds shown in the floating Report button. */
export type ReportKind = "pleasant" | "scenic" | "quiet" | "noisy" | "unsafe" | "crowded" | "inaccessible";

/** Map of report kind → sentiment + default 0-3 pleasantness signal. */
export const REPORT_KIND_META: Record<
  ReportKind,
  { label: string; sentiment: Sentiment; pleasantness: 0 | 1 | 2 | 3; Icon: LucideIcon }
> = {
  pleasant:     { label: "Pleasant",     sentiment: "positive", pleasantness: 3, Icon: Smile },
  scenic:       { label: "Scenic",       sentiment: "positive", pleasantness: 3, Icon: Trees },
  quiet:        { label: "Quiet",        sentiment: "positive", pleasantness: 2, Icon: VolumeX },
  noisy:        { label: "Noisy",        sentiment: "negative", pleasantness: 1, Icon: Volume2 },
  crowded:      { label: "Crowded",      sentiment: "negative", pleasantness: 1, Icon: Users },
  unsafe:       { label: "Unsafe",       sentiment: "negative", pleasantness: 0, Icon: AlertTriangle },
  inaccessible: { label: "Inaccessible", sentiment: "negative", pleasantness: 0, Icon: Accessibility },
};

/** Categorized pleasantness tags — what might make a place pleasant. */
export const POSITIVE_TAG_GROUPS = {
  "Atmosphere": [
    "Quiet", "Peaceful", "Lively but not loud", "Romantic", "Family-friendly",
  ],
  "Scenery & Nature": [
    "Great view", "Park/greenery", "Tree-lined", "Waterfront", "Flowers/gardens", "Wildlife",
  ],
  "Streetscape": [
    "Pretty street", "Historic charm", "Public art/murals", "Clean", "Wide sidewalks", "Pedestrian-only",
  ],
  "Things nearby": [
    "Good coffee", "Tasty food", "Cute shops", "Landmark", "Bookstore", "Bakery",
  ],
  "Comfort & Safety": [
    "Feels safe", "Well-lit", "Plenty of seating", "Shaded", "Accessible",
  ],
  "Travel quality": [
    "Good walking", "Good biking", "Smooth pavement", "Calm traffic", "Easy crossings",
  ],
} as const;

export const POSITIVE_TAGS = Object.values(POSITIVE_TAG_GROUPS).flat() as readonly string[];

export const NEGATIVE_TAG_GROUPS = {
  "Noise & Crowds": [
    "Too loud", "Overcrowded", "Rowdy",
  ],
  "Traffic & Streets": [
    "Heavy traffic", "Fast cars", "Bad sidewalk", "Bad bike lane", "Hard to cross",
  ],
  "Look & Feel": [
    "Ugly stretch", "Litter/dirty", "Graffiti (negative)", "Bad smell",
  ],
  "Safety": [
    "Unsafe", "Poorly lit", "Sketchy",
  ],
  "Other": [
    "Construction", "Too steep", "No shade", "No seating",
  ],
} as const;

export const NEGATIVE_TAGS = Object.values(NEGATIVE_TAG_GROUPS).flat() as readonly string[];

// ---------------------------------------------------------------------------
// Logging helpers
// ---------------------------------------------------------------------------
// Structured client-side logging for community writes. Every insert path
// emits a single grouped log line so support / dev can grep one keyword
// (`[community]`) and see: which call, which user-id source (anon vs auth),
// payload shape, latency, and the full Supabase error object (code, message,
// details, hint) when something fails.
//
// Errors here also surface as `console.error` so they show up red in
// browser devtools regardless of log level filtering.

interface SupabaseLikeError {
  message?: string;
  code?: string;
  details?: string;
  hint?: string;
  status?: number;
  statusCode?: number;
  name?: string;
}

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

function shapeError(err: unknown): SupabaseLikeError & { raw: unknown } {
  if (!isObject(err)) return { message: String(err), raw: err };
  return {
    message: typeof err.message === "string" ? err.message : undefined,
    code: typeof err.code === "string" ? err.code : undefined,
    details: typeof err.details === "string" ? err.details : undefined,
    hint: typeof err.hint === "string" ? err.hint : undefined,
    status: typeof err.status === "number" ? err.status : undefined,
    statusCode: typeof err.statusCode === "number" ? err.statusCode : undefined,
    name: typeof err.name === "string" ? err.name : undefined,
    raw: err,
  };
}

async function describeAuth(): Promise<{
  source: "auth" | "anon";
  userId: string;
  authUid: string | null;
}> {
  const userId = await getEffectiveUserId();
  let authUid: string | null = null;
  try {
    const { data } = await supabase.auth.getSession();
    authUid = data.session?.user?.id ?? null;
  } catch {
    /* ignore */
  }
  return {
    source: authUid ? "auth" : "anon",
    userId,
    authUid,
  };
}

function logCommunity(
  op: string,
  outcome: "ok" | "fail",
  fields: Record<string, unknown>,
): void {
  const tag = `[community:${op}]`;
  if (outcome === "ok") {
     
    console.info(tag, "ok", fields);
  } else {
     
    console.error(tag, "FAILED", fields);
  }
}

export async function submitTag(args: {
  lat: number;
  lng: number;
  mode: TravelMode;
  label: string;
  sentiment: Sentiment;
  pleasantness?: number;
  /** Free-text note the user wrote alongside the rating. Rides to the
   *  email notifier as body — ratings WITH words email Morgan, bare
   *  ratings don't (Aug 6: "only tags with notes on them and feedback"). */
  note?: string;
  /** R1: `user_confirmed_road` means the contributor picked the road out of
   *  the disambiguation picker, so the point is road-derived by an explicit
   *  human choice rather than by an automatic snap. It is a distinct value in
   *  the community_tags source CHECK and the R0/R1 identity trigger treats it
   *  as high confidence. `quick_report` and `poi_rating` remain in this union
   *  because callers still pass them; the database CHECK rejects the pair,
   *  which is a known parked mismatch, not something this change touches. */
  source?: "manual" | "in_trip_prompt" | "feedback" | "quick_report" | "poi_rating" | "user_confirmed_road";
  reportKind?: ReportKind;
  /** @deprecated Reporter weight is now set server-side via trigger. */
  reporterWeight?: number;
  /** Exact polyline-edge key (from `nearestPolylineSegment`) so the
   *  contribution attaches to the exact stretch of road the user is on,
   *  not just the coarse 50 m grid cell. */
  segmentKeyOverride?: string;
  /** The drive this tag was filed during — nav analytics' trip_id. Lets a
   *  tag join to its trip's endpoints, which is what made the parking-lot
   *  audit possible as a query instead of a guess. */
  tripId?: string | null;
  /** Metres from the raw GPS fix to the route at submit time. */
  snapDistM?: number | null;
  /** Name of the road this contribution resolved to, when there is one.
   *  Used for the notification only — never persisted (that is R2). */
  roadName?: string;
}) {
  const startedAt = performance.now();
  const auth = await describeAuth();
  const payload = {
    user_id: auth.userId,
    lat: args.lat,
    lng: args.lng,
    travel_mode: args.mode,
    tag_label: args.label,
    sentiment: args.sentiment,
    pleasantness: args.pleasantness ?? null,
    source: args.source ?? "manual",
    report_kind: args.reportKind ?? null,
    trip_id: args.tripId ?? null,
    snap_dist_m: args.snapDistM != null ? Math.round(args.snapDistM) : null,
    // reporter_weight is set by a server-side trigger — do not send from the client.
  };

  // Fast path: if the browser is offline we don't even attempt the
  // request — go straight to the queue.
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    enqueueTag({ payload, mode: args.mode, segmentKeyOverride: args.segmentKeyOverride });
    logCommunity("submitTag", "ok", {
      latencyMs: 0,
      auth,
      label: payload.tag_label,
      queued: true,
      reason: "offline",
    });
    return;
  }

  // Cast: generated types predate trip_id/snap_dist_m (added Aug 13,
  // verified live). Durable fix is regenerating types.ts.
  const { error } = await supabase.from("community_tags").insert(payload as never);
  const latencyMs = Math.round(performance.now() - startedAt);

  if (error) {
    const shaped = shapeError(error);
    const retryable = isRetryableWriteError(error);
    logCommunity("submitTag", "fail", {
      latencyMs,
      auth,
      payload: {
        ...payload,
        tag_label:
          payload.tag_label.length > 80
            ? `${payload.tag_label.slice(0, 80)}…`
            : payload.tag_label,
      },
      error: shaped,
      queued: retryable,
    });
    if (retryable) {
      enqueueTag({ payload, mode: args.mode, segmentKeyOverride: args.segmentKeyOverride });
      return; // resolve — caller's success path runs, queue toast informs the user
    }
    throw error;
  }

  logCommunity("submitTag", "ok", {
    latencyMs,
    auth,
    label: payload.tag_label,
    sentiment: payload.sentiment,
    source: payload.source,
    report_kind: payload.report_kind,
    coord: [payload.lng, payload.lat],
  });

  // Tell Morgan a road was tagged (July 31: "people are leaving reviews and
  // probably tagging roads and i'm not getting any emails"). Nothing was ever
  // wired here — notify-rating existed but was referenced by nothing at all.
  // Fire-and-forget; the tag is already saved.
  notifyContribution({
    kind: "tag",
    label: payload.tag_label,
    // The note IS the body — the server's mail filter (notify-rating v7)
    // emails tags with words and stays silent on bare ratings.
    body: args.note?.trim() || "",
    sentiment: payload.sentiment,
    pleasantness: (payload as { pleasantness?: number }).pleasantness ?? null,
    travelMode: args.mode,
    source: payload.source,
    // NO RAW CONTRIBUTOR COORDINATE (R1). We only claim a representative point
    // when the contribution actually resolved to a road — a segment key or a
    // recorded snap distance is the proof. Everything else sends no location
    // at all rather than quietly sending the fix.
    roadName: args.roadName,
    segmentRef: args.segmentKeyOverride,
    representativeLat: args.segmentKeyOverride || args.snapDistM != null ? payload.lat : undefined,
    representativeLng: args.segmentKeyOverride || args.snapDistM != null ? payload.lng : undefined,
  });

  // A Nice (2) or Gorgeous (3) tag is the happiest signal the app gets —
  // the right moment to ask for an App Store rating (Morgan, Aug 6: "we
  // are getting killed by two 1-star reviews"). Fire-and-forget; heavily
  // gated inside (native-only, cooldown, never mid-drive — a drive banks
  // the ask for the arrival recap).
  const p = (payload as { pleasantness?: number | null }).pleasantness;
  if (payload.sentiment === "positive" && typeof p === "number" && p >= 2) {
    void import("@/native/rateApp")
      .then((m) => m.requestReviewAfterPositiveTag({ midDrive: payload.source === "in_trip_prompt" }))
      .catch(() => {});
  }

  // Also feed the proprietary aggregated road-segment layer (signed-in users only).
  // Fire-and-forget — point report already succeeded. Wrap in catch so a
  // segment-layer failure still surfaces a structured log without breaking
  // the original call.
  void contributeToSegment({
    lat: args.lat,
    lng: args.lng,
    mode: args.mode,
    sentiment: args.sentiment,
    tagLabel: args.label,
    segmentKeyOverride: args.segmentKeyOverride,
  }).catch((segErr: unknown) => {
    logCommunity("contributeToSegment", "fail", {
      auth,
      coord: [args.lng, args.lat],
      error: shapeError(segErr),
    });
  });
}

/**
 * One-tap Waze-style report.
 *
 * R1: this no longer takes a raw position. The caller resolves a road first and
 * passes the road's representative point, so a quick report can never pin the
 * contributor. `report_kind` is what distinguishes these rows — the separate
 * 'quick_report' source value stays out of community_tags_source_check until
 * this path is mounted and its road/place semantics are settled.
 */
export async function submitQuickReport(args: {
  /** The ROAD's representative point, never the reporter's fix. */
  lat: number;
  lng: number;
  mode: TravelMode;
  kind: ReportKind;
  source: "manual" | "user_confirmed_road";
  segmentKeyOverride: string;
  snapDistM: number;
  roadName?: string;
}) {
  const meta = REPORT_KIND_META[args.kind];
  await submitTag({
    lat: args.lat,
    lng: args.lng,
    mode: args.mode,
    label: meta.label,
    sentiment: meta.sentiment,
    pleasantness: meta.pleasantness,
    source: args.source,
    reportKind: args.kind,
    segmentKeyOverride: args.segmentKeyOverride,
    snapDistM: args.snapDistM,
    roadName: args.roadName,
  });
}

/**
 * Quick 0-3 pleasantness rating from an in-trip prompt.
 * Scale labels (Avoid / Not nice / Fine / Nice / Gorgeous) collapse onto
 * the 4-value pleasantness column: 0, 1, 2, 2, 3.
 */
/** The previous rating this session — the stationary-repeat guard's memory. */
let lastRatingSubmit: { lat: number; lng: number; at: number } | null = null;

export async function submitPleasantnessRating(args: {
  lat: number;
  lng: number;
  mode: TravelMode;
  rating: 0 | 1 | 2 | 3;
  /** Optional human label for the spot ("Elm Street", "near Central Park"). */
  placeLabel?: string;
  /** Exact polyline-edge key for the active route. */
  segmentKeyOverride?: string;
  tripId?: string | null;
  snapDistM?: number | null;
  /**
   * WHAT WAS RATED — a road, or a place beside one (Aug 21).
   *
   * This defaulted to `in_trip_prompt` for every caller, so a rating of a cafe
   * filed at the CAFE's coordinates landed in community_tags indistinguishable
   * from a rating of the road someone was driving. That matters because of
   * rule 7: a negative tag within 150m of a route sets userAvoided, which is a
   * HARD BAN from the Scenic card. A cafe is almost always within 150m of its
   * own road — so disliking a coffee shop could permanently disqualify the
   * street it sits on.
   *
   * Measured Aug 21: 50 negative tags are actively banning routes and NOT ONE
   * carries a trip_id or a snap distance, so none of them can be shown to be
   * about a road at all. They are not all POI ratings either — "Must take
   * ferry, can't drive through" is in there, and that is a real road report.
   * The provenance simply was not recorded, so the two cannot be separated
   * after the fact.
   *
   * This does not try to. It stops the two being conflated from here on:
   * `poi_rating` is excluded from signals_along_route, so a place rating never
   * bans a road again. Existing rows are untouched and keep whatever effect
   * they already have — reclassifying them on a guess would be inventing
   * provenance we do not have (rule 22).
   */
  source?: "in_trip_prompt" | "poi_rating" | "user_confirmed_road";
}) {
  // THE STATIONARY-REPEAT GUARD (Morgan, Aug 13: houses and parking lots).
  //
  // Measured before the Aug 13 quarantine: 35 tags in 8 clusters where one
  // user filed 3+ ratings from the same ~100m spot — including Avoid AND
  // Gorgeous from a single point on a single day. A driver who is actually
  // driving cannot rate the same 120m twice inside ten minutes; only a parked
  // one can. Rating consecutive stretches mid-drive is untouched — you've
  // moved well past 120m within a minute at any driving speed.
  //
  // First rating from a spot always lands. Repeats from the same spot within
  // 10 minutes are dropped with a confession (rule 17), not queued.
  {
    const now = Date.now();
    const prev = lastRatingSubmit;
    if (prev && now - prev.at < 10 * 60_000) {
      const dLat = (args.lat - prev.lat) * 111_000;
      const dLng = (args.lng - prev.lng) * 111_000 * Math.cos((args.lat * Math.PI) / 180);
      const distM = Math.sqrt(dLat * dLat + dLng * dLng);
      if (distM < 120) {
        // eslint-disable-next-line no-console
        console.log(
          `[tag] stationary repeat DROPPED — ${Math.round(distM)}m from the previous ` +
            `rating ${Math.round((now - prev.at) / 1000)}s ago; one rating per spot per sitting`,
        );
        return;
      }
    }
    lastRatingSubmit = { lat: args.lat, lng: args.lng, at: now };
  }
  const sentiment: Sentiment = args.rating >= 2 ? "positive" : "negative";
  const label = args.placeLabel
    ? `${args.placeLabel} — ${args.rating}/3`
    : `Rated ${args.rating}/3`;
  await submitTag({
    lat: args.lat,
    lng: args.lng,
    mode: args.mode,
    label: label.slice(0, 60),
    sentiment,
    pleasantness: args.rating,
    source: args.source ?? "in_trip_prompt",
    segmentKeyOverride: args.segmentKeyOverride,
    tripId: args.tripId,
    snapDistM: args.snapDistM,
  });
}

export async function fetchTagsInBbox(b: {
  minLat: number; maxLat: number; minLng: number; maxLng: number;
}): Promise<CommunityTag[]> {
  // Use the SECURITY DEFINER RPC instead of selecting from community_tags
  // directly: the base table now restricts SELECT to row owners so we don't
  // leak contributor user_ids to anonymous viewers.
  const { data, error } = await supabase.rpc("community_tags_in_bbox", {
    min_lat: b.minLat, max_lat: b.maxLat,
    min_lng: b.minLng, max_lng: b.maxLng,
    max_rows: 500,
  });
  if (error) throw error;
  return (data ?? []) as CommunityTag[];
}

export async function submitFeedback(args: {
  mode: TravelMode;
  rating: number;
  tags: string[];
  start?: LngLat;
  end?: LngLat;
  /** Length of the rated route in metres — feeds the dashboard's
   *  "which route lengths rate well" card (Morgan, Aug 13). */
  routeDistanceM?: number | null;
  /** Which card was rated ("pleasant" | "fastest" | "scenic") and who built
   *  the route. Optional: callers that do not know say nothing, and the
   *  dashboard shows a dash rather than a guess (rule 20). */
  classification?: string | null;
  routeSource?: string | null;
  /** WHICH route was rated (Morgan, Aug 31) — the via signature from
   *  routeWaypointSignature(). With start/end it reconstructs the
   *  directions-cache key, so the rated route replays exactly. */
  routeWaypoints?: string | null;
  /** The rated card's pleasant score, and whether we recommended it. */
  pleasantScore?: number | null;
  isRecommended?: boolean | null;
}) {
  const startedAt = performance.now();
  const auth = await describeAuth();
  const payload = {
    user_id: auth.userId,
    travel_mode: args.mode,
    rating: args.rating,
    tags: args.tags,
    start_lat: args.start?.[1] ?? null,
    start_lng: args.start?.[0] ?? null,
    end_lat: args.end?.[1] ?? null,
    end_lng: args.end?.[0] ?? null,
    route_distance_m: args.routeDistanceM != null ? Math.round(args.routeDistanceM) : null,
    classification: args.classification ?? null,
    route_source: args.routeSource ?? null,
    route_waypoints: args.routeWaypoints ?? null,
    pleasant_score: args.pleasantScore ?? null,
    is_recommended: args.isRecommended ?? null,
    // Which build rated this (Morgan, Aug 7) — flows through the offline
    // queue too, since it lives in the payload.
    app_version: appVersion(),
  };

  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    enqueueFeedback({ payload });
    logCommunity("submitFeedback", "ok", {
      latencyMs: 0,
      auth,
      rating: args.rating,
      tag_count: args.tags.length,
      queued: true,
      reason: "offline",
    });
    return;
  }

  const { error } = await supabase.from("route_feedback").insert(payload);
  const latencyMs = Math.round(performance.now() - startedAt);

  if (error) {
    const retryable = isRetryableWriteError(error);
    logCommunity("submitFeedback", "fail", {
      latencyMs,
      auth,
      payload: { ...payload, tag_count: args.tags.length },
      error: shapeError(error),
      queued: retryable,
    });
    if (retryable) {
      enqueueFeedback({ payload });
      return;
    }
    throw error;
  }

  logCommunity("submitFeedback", "ok", {
    latencyMs,
    auth,
    rating: args.rating,
    tag_count: args.tags.length,
    mode: args.mode,
  });
}

/** Time-decay weight per spec */
export function recencyWeight(createdAt: string): number {
  const ageDays = (Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24);
  if (ageDays < 30) return 1.0;
  if (ageDays < 90) return 0.7;
  if (ageDays < 180) return 0.4;
  return 0.2;
}

export interface RouteSignal {
  id: string;
  lat: number;
  lng: number;
  pleasantness: number | null;
  report_kind: string | null;
  tag_label: string;
  sentiment: string;
  created_at: string;
  reporter_weight: number;
  recency_weight: number;
  effective_weight: number;
}

/**
 * Fetch community signals within `bufferM` meters of any sample point of a route.
 * Server returns weighted + recency-decayed rows ready for scoring/rendering.
 */
export async function fetchSignalsAlongRoute(
  samples: LngLat[],
  bufferM = 150,
  maxAgeDays = 90,
): Promise<RouteSignal[]> {
  if (samples.length === 0) return [];
  const sample_lats = samples.map((s) => s[1]);
  const sample_lngs = samples.map((s) => s[0]);
  const { data, error } = await supabase.rpc("signals_along_route", {
    sample_lats,
    sample_lngs,
    buffer_m: bufferM,
    max_age_days: maxAgeDays,
  });
  if (error) {
    console.warn("signals_along_route failed:", error.message);
    return [];
  }
  return (data ?? []) as RouteSignal[];
}
